import { Injectable, inject, signal, computed, OnDestroy, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BREAKPOINTS, DEVICE_BREAKPOINTS, type BreakpointKey, type DeviceType } from '../constants/breakpoints';

/**
 * High-performance responsive service for zoneless Angular applications
 * Provides real-time window width monitoring with SSR safety
 */
@Injectable({
  providedIn: 'root'
})
export class ResponsiveService implements OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  
  // Core width signal
  private readonly _width = signal<number>(this.getInitialWidth());
  
  // Debouncing
  private resizeTimeout: number | null = null;
  private rafId: number | null = null;
  
  // Event listener reference for cleanup
  private resizeListener: (() => void) | null = null;

  constructor() {
    if (this.isBrowser) {
      this.initializeResizeListener();
    }
  }

  /**
   * Current window width as a signal
   */
  public readonly width = this._width.asReadonly();

  /**
   * Computed signals for device types
   */
  public readonly isMobile = computed(() => {
    const width = this._width();
    return width >= DEVICE_BREAKPOINTS.mobile.min && width <= DEVICE_BREAKPOINTS.mobile.max;
  });

  public readonly isTablet = computed(() => {
    const width = this._width();
    return width >= DEVICE_BREAKPOINTS.tablet.min && width <= DEVICE_BREAKPOINTS.tablet.max;
  });

  public readonly isDesktop = computed(() => {
    const width = this._width();
    return width >= DEVICE_BREAKPOINTS.desktop.min && width <= DEVICE_BREAKPOINTS.desktop.max;
  });

  /**
   * Computed signals for breakpoints
   */
  public readonly isXs = computed(() => this._width() < BREAKPOINTS.sm);
  public readonly isSm = computed(() => this._width() >= BREAKPOINTS.sm && this._width() < BREAKPOINTS.md);
  public readonly isMd = computed(() => this._width() >= BREAKPOINTS.md && this._width() < BREAKPOINTS.lg);
  public readonly isLg = computed(() => this._width() >= BREAKPOINTS.lg && this._width() < BREAKPOINTS.xl);
  public readonly isXl = computed(() => this._width() >= BREAKPOINTS.xl && this._width() < BREAKPOINTS['2xl']);
  public readonly is2xl = computed(() => this._width() >= BREAKPOINTS['2xl']);

  /**
   * Check if current width matches a specific breakpoint
   */
  public isBreakpoint(breakpoint: BreakpointKey): boolean {
    const width = this._width();
    const breakpointValue = BREAKPOINTS[breakpoint];
    
    switch (breakpoint) {
      case 'xs':
        return width < BREAKPOINTS.sm;
      case 'sm':
        return width >= BREAKPOINTS.sm && width < BREAKPOINTS.md;
      case 'md':
        return width >= BREAKPOINTS.md && width < BREAKPOINTS.lg;
      case 'lg':
        return width >= BREAKPOINTS.lg && width < BREAKPOINTS.xl;
      case 'xl':
        return width >= BREAKPOINTS.xl && width < BREAKPOINTS['2xl'];
      case '2xl':
        return width >= BREAKPOINTS['2xl'];
      default:
        return false;
    }
  }

  /**
   * Check if current width is within a specific range
   */
  public isWidthBetween(min: number, max: number): boolean {
    const width = this._width();
    return width >= min && width <= max;
  }

  /**
   * Get current device type
   */
  public getCurrentDevice(): DeviceType {
    const width = this._width();
    
    if (width <= DEVICE_BREAKPOINTS.mobile.max) return 'mobile';
    if (width <= DEVICE_BREAKPOINTS.tablet.max) return 'tablet';
    return 'desktop';
  }

  /**
   * Get current breakpoint key
   */
  public getCurrentBreakpoint(): BreakpointKey {
    const width = this._width();
    
    if (width < BREAKPOINTS.sm) return 'xs';
    if (width < BREAKPOINTS.md) return 'sm';
    if (width < BREAKPOINTS.lg) return 'md';
    if (width < BREAKPOINTS.xl) return 'lg';
    if (width < BREAKPOINTS['2xl']) return 'xl';
    return '2xl';
  }

  /**
   * Get initial width (SSR-safe)
   */
  private getInitialWidth(): number {
    if (!this.isBrowser) {
      return 1024; // Default desktop width for SSR
    }
    return window.innerWidth;
  }

  /**
   * Initialize resize listener with performance optimizations
   */
  private initializeResizeListener(): void {
    if (!this.isBrowser) return;

    this.resizeListener = () => {
      // Cancel previous RAF to prevent multiple updates
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }

      // Use RAF for smooth updates
      this.rafId = requestAnimationFrame(() => {
        const newWidth = window.innerWidth;
        const currentWidth = this._width();
        
        // Only update if width actually changed
        if (newWidth !== currentWidth) {
          this._width.set(newWidth);
        }
      });
    };

    // Add passive listener for better performance
    window.addEventListener('resize', this.resizeListener, { passive: true });
  }

  /**
   * Cleanup resources
   */
  ngOnDestroy(): void {
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    if (this.resizeListener && this.isBrowser) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }
} 